import React, { useState } from 'react';
import styles from './Experience.module.css';

interface Job {
  company:    string;
  role:       string;
  period:     string;
  location:   string;
  highlights: string[];
  tech:       string[];
}

const JOBS: Job[] = [
  {
    company:  'Mediconsult Oy',
    role:     'Full Stack Software Engineer',
    period:   '05/2024 – 07/2025',
    location: 'Finland',
    highlights: [
      'Developed Java backend services for a Finnish healthcare platform',
      'Designed RESTful APIs and integrated with Angular frontend',
      'Contributed to performance improvements and system reliability',
      'Worked in cross-functional agile environment with strict medical compliance standards',
    ],
    tech: ['Java', 'Spring Boot', 'Angular', 'REST API', 'MariaDB', 'PostgreSQL', 'Docker'],
  },
  {
    company:  'Endline Solutions',
    role:     'Senior Web Developer',
    period:   '12/2022 – 01/2024',
    location: 'United States (Remote)',
    highlights: [
      'Developed backend services using Node.js, TypeScript, and Express',
      'Built REST APIs for POS and payment processing systems',
      'Improved system performance and optimized database queries',
      'Implemented real-time features using WebSockets',
    ],
    tech: ['Node.js', 'TypeScript', 'React', 'Express', 'WebSockets', 'PostgreSQL', 'MongoDB'],
  },
  {
    company:  'Pearson',
    role:     'Senior Software Engineer',
    period:   '04/2023 – 08/2025',
    location: 'Remote',
    highlights: [
      'Integrated LaunchDarkly for feature flag management across production services',
      'Migrated microservices to AWS using Kafka and SQS, improving scalability and resilience',
      'Increased system reliability to 99.9% uptime through robust incident management',
      'Improved observability using New Relic, Grafana, and CloudWatch (ServiceNow integration)',
      'Conducted code reviews and mentored junior developers in agile CI/CD environment (Jenkins, Docker, ECS)',
    ],
    tech: ['Kafka', 'SQS', 'AWS', 'Docker', 'Jenkins', 'New Relic', 'LaunchDarkly'],
  },
  {
    company:  'Pearson',
    role:     'Software Engineer',
    period:   '04/2022 – 04/2023',
    location: 'Remote',
    highlights: [
      'Developed microservices using Spring Boot and Hibernate',
      'Built SQS-based async workflows and CI/CD pipelines with Docker deployments',
      'Implemented Redis caching strategies, reducing API response time by 30%',
      'Collaborated with cross-functional teams to deliver scalable solutions',
    ],
    tech: ['Spring Boot', 'Hibernate', 'SQS', 'Redis', 'Docker', 'CI/CD'],
  },
  {
    company:  'Pearson',
    role:     'Associate Software Engineer',
    period:   '04/2021 – 04/2022',
    location: 'Remote',
    highlights: [
      'Worked with AWS S3, ECS, and CloudWatch for cloud-native deployments',
      'Built and maintained microservices with Jenkins and Docker',
    ],
    tech: ['AWS S3', 'ECS', 'CloudWatch', 'Jenkins', 'Docker'],
  },
  {
    company:  'EMINE Solutions',
    role:     'Junior Fullstack Developer',
    period:   '01/2018 – 07/2020',
    location: 'On-site',
    highlights: [
      'Developed Node.js/Express APIs for production applications',
      'Built Angular features and maintained production codebase',
    ],
    tech: ['Node.js', 'Express', 'Angular', 'MySQL'],
  },
];

export default function Experience(): React.ReactElement {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const job = JOBS[activeIndex];

  return (
    <section id="experience">
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          <span className={styles.headingNum}>02.</span>
          Experience
          <div className={styles.headingLine} />
        </h2>

        <div className={styles.layout}>
          {/* Tab buttons */}
          <div className={styles.tabs} role="tablist" aria-label="Job history">
            {JOBS.map(({ company, role }, i) => (
              <button
                key={`${company}-${role}-${i}`}
                role="tab"
                aria-selected={i === activeIndex}
                className={`${styles.tab}${i === activeIndex ? ` ${styles.tabActive}` : ''}`}
                onClick={() => setActiveIndex(i)}
              >
                {company}
              </button>
            ))}
          </div>

          {/* Content panel */}
          <div className={styles.panel} role="tabpanel">
            <h3 className={styles.role}>
              {job.role}{' '}
              <span className={styles.roleCompany}>@ {job.company}</span>
            </h3>
            <p className={styles.meta}>
              {job.period} · {job.location}
            </p>

            <ul className={styles.highlights}>
              {job.highlights.map((h, i) => (
                <li key={i} className={styles.highlightItem}>
                  <span className={styles.arrow} aria-hidden="true">▹</span>
                  {h}
                </li>
              ))}
            </ul>

            <div className={styles.techList}>
              {job.tech.map((t) => (
                <span key={t} className={styles.techTag}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
